import {
  useCallback,
  useEffect,
  useState,
  createElement,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { Command, MenuDisplayProps } from './types';

import { BloxIconColorsType } from 'themes/constants';
import { useDeprecatedAssets } from 'utility/hooks/useDeprecatedAssets';

import {
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  useTheme,
} from '@mui/material';
import { radiusQuarter } from 'utility/borderRadius';

export const MenuDisplay = forwardRef((props: MenuDisplayProps, ref) => {
  const theme = useTheme();
  const { deprecatedAssets } = useDeprecatedAssets();
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

  const selectItem = useCallback(
    (groupIndex: number, commandIndex: number) => {
      const command = props.items[groupIndex].commands[commandIndex];
      props.command(command);
    },
    [props],
  );

  const createCommandClickHandler = useCallback(
    (groupIndex: number, commandIndex: number) => {
      return () => {
        selectItem(groupIndex, commandIndex);
      };
    },
    [selectItem],
  );

  const navigateMenu = (direction: 'up' | 'down') => {
    if (direction === 'down') {
      if (!props.items.length) {
        return false;
      }

      const commands = props.items[selectedGroupIndex].commands;

      let newCommandIndex = selectedCommandIndex + 1;
      let newGroupIndex = selectedGroupIndex;

      if (commands.length - 1 < newCommandIndex) {
        newCommandIndex = 0;
        newGroupIndex = selectedGroupIndex + 1;
      }

      if (props.items.length - 1 < newGroupIndex) {
        newGroupIndex = 0;
      }

      setSelectedCommandIndex(newCommandIndex);
      setSelectedGroupIndex(newGroupIndex);

      return true;
    }

    if (direction === 'up') {
      if (!props.items.length) {
        return false;
      }

      let newCommandIndex = selectedCommandIndex - 1;
      let newGroupIndex = selectedGroupIndex;

      if (newCommandIndex < 0) {
        newGroupIndex = selectedGroupIndex - 1;
        newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0;
      }

      if (newGroupIndex < 0) {
        newGroupIndex = props.items.length - 1;
        newCommandIndex = props.items[newGroupIndex].commands.length - 1;
      }

      setSelectedCommandIndex(newCommandIndex);
      setSelectedGroupIndex(newGroupIndex);

      return true;
    }
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === 'ArrowDown') {
        return navigateMenu('down');
      }

      if (event.key === 'ArrowUp') {
        return navigateMenu('up');
      }

      if (event.key === 'Enter') {
        if (
          !props.items.length ||
          selectedGroupIndex === -1 ||
          selectedCommandIndex === -1
        ) {
          return false;
        }

        selectItem(selectedGroupIndex, selectedCommandIndex);

        return true;
      }

      return false;
    },
  }));

  // Anytime the groups change, i.e. the user types to narrow it down, we want to
  // reset the current selection to the first menu item
  useEffect(() => {
    setSelectedGroupIndex(0);
    setSelectedCommandIndex(0);
  }, [props.items]);

  if (!props.items.length) {
    return null;
  }

  return (
    <MenuList
      aria-label="Editorial Content Item Menu Actions"
      className="fab_content_menu_actions"
      key="slash-command-menu-list"
      sx={(theme) => ({
        padding: 0,
        overflowY: 'auto',
        maxHeight: 300,
        width: 250,
        borderRadius: radiusQuarter(theme),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        '& .MuiListItem-root': {
          px: 1 / 2,
          py: 1 / 4,
        },
        '& .MuiListItemIcon-root': {
          minWidth: '0 !important',
        },
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[300],
          borderRadius: radiusQuarter(theme),
        },
        '& .MuiSvgIcon-root , & .MuiTypography-root': {
          fontSize: '1rem',
        },
        '& .MuiButtonBase-root': {
          gap: 1,
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
          },
        },
      })}
    >
      {props.items.map((group, groupIndex: number) => (
        <div key={groupIndex}>
          <ListSubheader
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontSize: '1.125rem',
              lineHeight: '2.5rem',
            }}
          >
            {group.title}
          </ListSubheader>
          {group.commands.map((command: Command, commandIndex: number) => {
            // Don't show the command if:
            // 1. It's a deprecated asset type (key exists in the object)
            if (deprecatedAssets?.hasOwnProperty(command.name)) {
              return null;
            }

            return (
              <MenuItem
                key={`${groupIndex}-${commandIndex}`}
                selected={
                  selectedGroupIndex === groupIndex &&
                  selectedCommandIndex === commandIndex
                }
                onClick={createCommandClickHandler(groupIndex, commandIndex)}
              >
                <ListItemIcon>
                  {command.icon &&
                    createElement(command.icon, {
                      sx: {
                        color: command.iconColor
                          ? theme.blox.iconColors[
                              command.iconColor as keyof BloxIconColorsType
                            ]
                          : theme.palette.text.primary,
                      },
                    })}
                </ListItemIcon>
                <ListItemText
                  primary={command.label}
                  sx={{ color: theme.palette.text.primary }}
                />
              </MenuItem>
            );
          })}
        </div>
      ))}
    </MenuList>
  );
});

export default MenuDisplay;
